import type { FlexProps } from 'antd';
import { theme } from 'antd';
import { neutralColors, worktoolDecorations } from '../../config/theme';
import { createComponentPresets } from './presets';

export type AppTheme = ReturnType<typeof useAppTheme>;

export interface ResponsiveColumns {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
}

export interface LayoutPresets {
  flexContainer: (direction?: 'row' | 'column') => FlexProps;
  responsiveGrid: (columns: ResponsiveColumns) => FlexProps;
  centeredContent: (maxWidth?: number) => FlexProps;
  cardLayout: () => FlexProps;
  sectionContainer: () => FlexProps;
  heroLayout: () => FlexProps;
  stackWithResponsiveGap: (
    defaultGap?: number,
    mobileGap?: number,
  ) => FlexProps;
}

export interface SpacingSystem {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
  xxl: number;
}

export interface TypographySystem {
  heading1: React.CSSProperties;
  heading2: React.CSSProperties;
  heading3: React.CSSProperties;
  heading4: React.CSSProperties;
  heading5: React.CSSProperties;
  body: React.CSSProperties;
  caption: React.CSSProperties;
  code: React.CSSProperties;
}

/**
 * Enhanced useAppTheme with FastGPT-inspired design system
 * Provides comprehensive theme utilities, Flex-based layout presets,
 * and sophisticated color management with dark mode support
 */
export function useAppTheme() {
  const { token } = theme.useToken();

  // Centralized breakpoints and media helpers
  const breakpoints = {
    xs: token.screenXS || 480,
    sm: token.screenSM || 576,
    md: token.screenMD || 768,
    lg: token.screenLG || 992,
    xl: token.screenXL || 1200,
    xxl: token.screenXXL || 1600,
  } as const;

  const media = {
    up: (key: keyof typeof breakpoints) =>
      `@media (min-width: ${breakpoints[key]}px)` as const,
    down: (key: keyof typeof breakpoints) =>
      `@media (max-width: ${breakpoints[key]}px)` as const,
    between: (min: keyof typeof breakpoints, max: keyof typeof breakpoints) =>
      `@media (min-width: ${breakpoints[min]}px) and (max-width: ${breakpoints[max]}px)` as const,
  };

  // Enhanced color system with FastGPT-inspired neutral scale
  const colors = {
    primary: token.colorPrimary,
    primaryHover: token.colorPrimaryHover,
    primaryActive: token.colorPrimaryActive,
    primaryBg: token.colorPrimaryBg,
    text: token.colorText,
    textSecondary: token.colorTextSecondary,
    textTertiary: token.colorTextTertiary,
    textQuaternary: token.colorTextQuaternary,
    bg: token.colorBgContainer,
    bgElevated: token.colorBgElevated,
    layoutBg: token.colorBgLayout,
    border: token.colorBorder,
    borderSecondary: token.colorBorderSecondary,

    // FastGPT-inspired neutral scale
    neutral: neutralColors,

    // Legacy support for existing components
    black: neutralColors,
    gray: neutralColors,
    // Project custom colors
    blueSectionBg: '#F5F9FF',
  };

  // Enhanced gradient system
  const gradient = {
    primary: worktoolDecorations.primaryGradient,
    primaryHover: worktoolDecorations.primaryGradientHover,
    soft: worktoolDecorations.softGradient,
    neutral: worktoolDecorations.neutralGradient,
    dark: worktoolDecorations.darkGradient,
    darkSoft: worktoolDecorations.darkSoftGradient,
    companyIntro: worktoolDecorations.companyIntroGradient,
  };

  // Enhanced shadow system
  const shadow = {
    card: token.boxShadow,
    elevated: token.boxShadowSecondary,
    hero: worktoolDecorations.heroGlowShadow,
    cardHover: worktoolDecorations.cardHoverShadow,
    focus: worktoolDecorations.focusRing,
  };

  // Spacing system
  const spacing: SpacingSystem = {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
  };

  // Typography system
  const typography: TypographySystem = {
    heading1: {
      fontSize: token.fontSizeHeading1 || 32,
      fontWeight: token.fontWeightStrong || 600,
      lineHeight: 1.2,
      color: token.colorTextHeading || token.colorText,
      margin: 0,
    },
    heading2: {
      fontSize: token.fontSizeHeading2 || 24,
      fontWeight: token.fontWeightStrong || 600,
      lineHeight: 1.2,
      color: token.colorTextHeading || token.colorText,
      margin: 0,
    },
    heading3: {
      fontSize: token.fontSizeHeading3 || 20,
      fontWeight: token.fontWeightStrong || 600,
      lineHeight: 1.2,
      color: token.colorTextHeading || token.colorText,
      margin: 0,
    },
    heading4: {
      fontSize: token.fontSizeHeading4 || 16,
      fontWeight: token.fontWeightStrong || 600,
      lineHeight: 1.2,
      color: token.colorTextHeading || token.colorText,
      margin: 0,
    },
    heading5: {
      fontSize: token.fontSizeHeading5 || 14,
      fontWeight: token.fontWeightStrong || 600,
      lineHeight: 1.2,
      color: token.colorTextHeading || token.colorText,
      margin: 0,
    },
    body: {
      fontSize: token.fontSize || 14,
      lineHeight: token.lineHeight || 1.5714,
      color: token.colorText,
      margin: 0,
    },
    caption: {
      fontSize: token.fontSizeSM || 12,
      lineHeight: token.lineHeightSM || 1.66,
      color: token.colorTextSecondary,
      margin: 0,
    },
    code: {
      fontFamily: token.fontFamilyCode || 'monospace',
      fontSize: token.fontSizeSM || 12,
      backgroundColor: token.colorBgSpotlight,
      padding: '2px 4px',
      borderRadius: token.borderRadiusXS || 4,
      border: `1px solid ${token.colorBorderSecondary}`,
    },
  };

  // Flex-based layout presets
  const layout: LayoutPresets = {
    flexContainer: (direction = 'row') => ({
      vertical: direction === 'column',
      gap: spacing.md,
      align: 'stretch',
      style: {
        width: '100%',
      },
    }),

    responsiveGrid: (columns) => ({
      wrap: 'wrap',
      gap: spacing.md,
      style: {
        '--grid-columns-xs': columns.xs || 1,
        '--grid-columns-sm': columns.sm || 2,
        '--grid-columns-md': columns.md || 3,
        '--grid-columns-lg': columns.lg || 4,
        '--grid-columns-xl': columns.xl || 4,
      } as React.CSSProperties,
    }),

    centeredContent: (maxWidth = 1200) => ({
      vertical: false,
      justify: 'center',
      style: {
        maxWidth,
        margin: '0 auto',
        padding: `0 ${spacing.md}px`,
        width: '100%',
      },
    }),

    cardLayout: () => ({
      vertical: true,
      gap: spacing.lg,
      style: {
        backgroundColor: token.colorBgContainer,
        borderRadius: token.borderRadiusLG,
        padding: spacing.lg,
        boxShadow: shadow.card,
        border: `1px solid ${token.colorBorderSecondary}`,
      },
    }),

    sectionContainer: () => ({
      vertical: true,
      gap: spacing.xxl,
      style: {
        padding: `${spacing.xxl * 1.5}px 0`,
        width: '100%',
      },
    }),

    heroLayout: () => ({
      vertical: true,
      align: 'center',
      justify: 'center',
      gap: spacing.xl,
      style: {
        minHeight: '60vh',
        textAlign: 'center' as const,
        padding: `${spacing.xxl}px ${spacing.md}px`,
      },
    }),

    stackWithResponsiveGap: (
      defaultGap = spacing.xl,
      _mobileGap = spacing.lg,
    ) => ({
      vertical: true,
      style: {
        width: '100%',
        gap: defaultGap,
      },
    }),
  };

  // Enhanced utility functions
  const utils = {
    resolveColor: (input?: string): string => {
      if (!input) return colors.primary;
      const value = String(input).trim();
      if (
        value.startsWith('#') ||
        value.startsWith('rgb') ||
        value.startsWith('hsl')
      )
        return value;

      // Handle dot notation (e.g., 'neutral.500', 'primary')
      if (value.includes('.')) {
        const [family, tone] = value.split('.');
        const palette: any = (colors as any)[family];
        if (palette?.[tone as any]) return palette[tone as any];
      }

      // Direct color access
      const direct = (colors as any)[value];
      return direct || value;
    },

    getResponsiveValue: (
      values: ResponsiveColumns,
      breakpoint: keyof ResponsiveColumns = 'md',
    ) => {
      return values[breakpoint] || values.md || values.sm || values.xs || 1;
    },

    createFlexBasis: (columns: number, gap: number = spacing.md) => {
      if (columns === 1) return '100%';
      const gapTotal = (columns - 1) * gap;
      return `calc(${100 / columns}% - ${gapTotal / columns}px)`;
    },
  };

  // Enhanced component presets
  const presets = {
    // Layout presets
    section: {
      padding: `${spacing.xxl * 1.2}px 0`,
      width: '100%',
    } as React.CSSProperties,

    container: {
      maxWidth: 1200,
      margin: '0 auto',
      padding: `0 ${spacing.md}px`,
      width: '100%',
    } as React.CSSProperties,

    // Card presets
    card: {
      backgroundColor: token.colorBgContainer,
      borderRadius: token.borderRadiusLG,
      padding: spacing.lg,
      boxShadow: shadow.card,
      border: `1px solid ${token.colorBorderSecondary}`,
      transition: `all ${token.motionDurationMid}`,
    } as React.CSSProperties,

    cardHover: {
      boxShadow: shadow.cardHover,
      transform: 'translateY(-2px)',
    } as React.CSSProperties,

    // Button presets
    buttonPrimary: {
      background: gradient.primary,
      border: 'none',
      color: '#fff',
      borderRadius: token.borderRadius,
      padding: `${spacing.sm}px ${spacing.md}px`,
      fontWeight: 500,
      cursor: 'pointer',
      transition: `all ${token.motionDurationMid}`,
    } as React.CSSProperties,

    buttonSecondary: {
      background: 'transparent',
      border: `1px solid ${token.colorBorder}`,
      color: token.colorText,
      borderRadius: token.borderRadius,
      padding: `${spacing.sm}px ${spacing.md}px`,
      fontWeight: 500,
      cursor: 'pointer',
      transition: `all ${token.motionDurationMid}`,
    } as React.CSSProperties,

    // Interactive states
    hover: {
      backgroundColor: worktoolDecorations.hoverOverlay,
    } as React.CSSProperties,

    active: {
      backgroundColor: worktoolDecorations.activeOverlay,
    } as React.CSSProperties,

    focus: {
      outline: 'none',
      boxShadow: worktoolDecorations.focusRing,
    } as React.CSSProperties,
  };

  // Component presets with FastGPT aesthetics
  const components = createComponentPresets(token);

  // Zero-config layout constants for direct spread usage
  const layouts = {
    sectionContainer: {
      vertical: true,
      style: {
        padding: `${spacing.md}px 0`,
        width: '100%',
      },
    } as FlexProps,

    centeredContent: {
      vertical: false,
      justify: 'center',
      style: {
        maxWidth: 1200,
        margin: '0 auto',
        padding: `0 ${spacing.md}px`,
        width: '100%',
      },
    } as FlexProps,

    stack: {
      vertical: true,
      style: {
        width: '100%',
        gap: spacing.xl,
      },
    } as FlexProps,

    // Common rows
    rowBetween: {
      vertical: false,
      justify: 'space-between',
      align: 'center',
      wrap: 'wrap',
      style: {
        width: '100%',
        gap: spacing.md,
      },
    } as FlexProps,

    rowCenter: {
      vertical: false,
      justify: 'center',
      align: 'center',
      wrap: 'wrap',
      style: {
        width: '100%',
        gap: spacing.md,
      },
    } as FlexProps,

    columnCenter: {
      vertical: true,
      align: 'center',
      style: {
        width: '100%',
        gap: spacing.md,
      },
    } as FlexProps,

    // Containers
    containerNarrow: {
      vertical: false,
      justify: 'center',
      style: {
        maxWidth: 960,
        margin: '0 auto',
        padding: `0 ${spacing.md}px`,
        width: '100%',
      },
    } as FlexProps,

    // Stacks
    stackTight: {
      vertical: true,
      style: {
        width: '100%',
        gap: spacing.md,
      },
    } as FlexProps,

    // Responsive grids (Flex wrap with sensible gaps)
    grid2: {
      ...layout.responsiveGrid({ xs: 1, sm: 2, md: 2, lg: 2, xl: 2 }),
    } as FlexProps,
    grid3: {
      ...layout.responsiveGrid({ xs: 1, sm: 2, md: 3, lg: 3, xl: 3 }),
    } as FlexProps,
    grid4: {
      ...layout.responsiveGrid({ xs: 1, sm: 2, md: 3, lg: 4, xl: 4 }),
    } as FlexProps,
  } as const;

  return {
    token,
    colors,
    gradient,
    shadow,
    spacing,
    typography,
    layout,
    presets,
    components,
    utils,
    breakpoints,
    media,
    layouts,
  };
}

// Export utility hooks
export * from './hooks';

export default useAppTheme;
